<template>
    <div class="appstore-reviews-page">
        <!-- Left Panel: Reviews -->
        <div class="reviews-panel">
            <div class="panel-header">
                <h2 class="panel-title">
                    <span class="material-icons apple-icon">star</span>
                    {{ $t("appStore.reviews") }}
                    <span class="count-badge">{{ total }}</span>
                </h2>
                <div class="header-actions">
                    <a-select
                        v-model="filters.appId"
                        :placeholder="$t('appStore.selectApp')"
                        style="width: 200px"
                        size="small"
                        allowClear
                    >
                        <a-select-option value="">{{ $t("appStore.allApps") }}</a-select-option>
                        <a-select-option v-for="app in apps" :key="app._id" :value="app.appId">
                            {{ app.name | truncate(40) }}
                        </a-select-option>
                    </a-select>
                    <a-select v-model="filters.status" style="width: 120px" size="small">
                        <a-select-option value="">{{ $t("appStore.all") }}</a-select-option>
                        <a-select-option value="new">{{ $t("appStore.new") }}</a-select-option>
                        <a-select-option value="replied">{{ $t("appStore.replied") }}</a-select-option>
                        <a-select-option value="resolved">{{ $t("appStore.resolved") }}</a-select-option>
                    </a-select>
                    <a-button size="small" @click="fetchReviews" :loading="loading">
                        <a-icon type="reload" />
                    </a-button>
                </div>
            </div>

            <div class="reviews-list" v-if="!loading">
                <div
                    class="review-item"
                    v-for="review in reviews"
                    :key="review._id"
                    :class="{ selected: selectedReview && selectedReview._id === review._id }"
                    @click="selectReview(review)"
                >
                    <span class="material-icons avatar-placeholder">account_circle</span>
                    <div class="review-main">
                        <div class="review-top">
                            <span class="author-name">{{ review.reviewerNickname || $t("appStore.anonymous") }}</span>
                            <span class="review-date">{{ formatDate(review.createdDate) }}</span>
                            <span class="status-dot" :class="review.status" :title="getStatusLabel(review.status)"></span>
                        </div>
                        <div class="star-rating">
                            <span class="material-icons" v-for="n in review.rating" :key="'filled-' + n">star</span>
                            <span class="material-icons empty" v-for="n in (5 - review.rating)" :key="'empty-' + n">star_border</span>
                        </div>
                        <p class="review-title" v-if="review.title">{{ review.title }}</p>
                        <p class="review-text" v-if="review.body">{{ truncateText(review.body, 120) }}</p>
                        <p class="review-text no-comment" v-else>{{ $t("appStore.noComment") }}</p>
                        <div class="review-bottom">
                            <span class="app-link" v-if="review.app">
                                <span class="material-icons">phone_iphone</span> {{ review.app.name | truncate(25) }}
                            </span>
                            <span v-if="review.territory" class="territory-badge">
                                {{ review.territory }}
                            </span>
                        </div>
                    </div>
                    <div class="review-actions-mini">
                        <a-dropdown :trigger="['click']" @click.stop>
                            <a-button size="small" type="link"><a-icon type="more" /></a-button>
                            <template #overlay>
                                <a-menu>
                                    <a-sub-menu key="status" :title="$t('appStore.status')">
                                        <a-menu-item key="new" @click="updateStatus(review, 'new')">
                                            <span class="status-indicator new"></span> {{ $t("appStore.new") }}
                                        </a-menu-item>
                                        <a-menu-item key="replied" @click="updateStatus(review, 'replied')">
                                            <span class="status-indicator replied"></span> {{ $t("appStore.replied") }}
                                        </a-menu-item>
                                        <a-menu-item key="resolved" @click="updateStatus(review, 'resolved')">
                                            <span class="status-indicator resolved"></span> {{ $t("appStore.resolved") }}
                                        </a-menu-item>
                                    </a-sub-menu>
                                    <a-menu-divider />
                                    <a-menu-item key="reply" @click="openReplyModal(review)">
                                        <a-icon type="message" /> {{ $t("appStore.reply") }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </div>

                <div class="empty-state" v-if="reviews.length === 0">
                    <span class="material-icons">reviews</span>
                    <p>{{ $t("appStore.noReviewsFound") }}</p>
                </div>
            </div>

            <div class="loading-state" v-else>
                <a-spin />
            </div>

            <div class="pagination-wrapper" v-if="total > pagination.limit">
                <a-pagination
                    :current="pagination.page"
                    :total="total"
                    :page-size="pagination.limit"
                    size="small"
                    @change="onPageChange"
                    @showSizeChange="onPageSizeChange"
                    show-size-changer
                    :page-size-options="['20', '50', '100']"
                />
            </div>
        </div>

        <!-- Right Panel: Review Detail -->
        <div class="detail-panel" v-if="selectedReview">
            <div class="detail-header">
                <span class="material-icons">account_circle</span>
                <div class="author-details">
                    <span class="name">{{ selectedReview.reviewerNickname || $t("appStore.anonymous") }}</span>
                    <span class="date">{{ formatDate(selectedReview.createdDate) }}</span>
                </div>
                <div class="badges">
                    <span class="status-badge" :class="selectedReview.status">{{ getStatusLabel(selectedReview.status) }}</span>
                </div>
            </div>

            <div class="star-rating-large">
                <span class="material-icons" v-for="n in selectedReview.rating" :key="'filled-' + n">star</span>
                <span class="material-icons empty" v-for="n in (5 - selectedReview.rating)" :key="'empty-' + n">star_border</span>
            </div>

            <div class="detail-title" v-if="selectedReview.title">{{ selectedReview.title }}</div>
            <div class="detail-body" v-if="selectedReview.body">{{ selectedReview.body }}</div>
            <div class="detail-body no-comment" v-else>{{ $t("appStore.noComment") }}</div>

            <!-- Territory Info -->
            <div class="territory-info" v-if="selectedReview.territory">
                <div class="section-title">{{ $t("appStore.territory") }}</div>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label">{{ $t("appStore.region") }}:</span>
                        <span class="value">{{ selectedReview.territory }}</span>
                    </div>
                </div>
            </div>

            <div class="reply-section" v-if="selectedReview.response && selectedReview.response.body">
                <div class="reply-header">
                    <span class="material-icons">reply</span>
                    {{ $t("appStore.yourReply") }}
                    <span class="reply-state" :class="selectedReview.response.state">
                        {{ selectedReview.response.state }}
                    </span>
                </div>
                <div class="reply-content">{{ selectedReview.response.body }}</div>
                <div class="reply-date">{{ formatDate(selectedReview.response.lastModifiedDate) }}</div>
                <div class="reply-actions">
                    <a-button size="small" @click="openReplyModal(selectedReview, true)">
                        <a-icon type="edit" /> {{ $t("appStore.edit") }}
                    </a-button>
                    <a-button size="small" type="danger" @click="confirmDeleteResponse(selectedReview)">
                        <a-icon type="delete" /> {{ $t("appStore.delete") }}
                    </a-button>
                </div>
            </div>

            <div class="detail-actions">
                <a-button size="small" type="primary" @click="openReplyModal(selectedReview)" v-if="!selectedReview.response || !selectedReview.response.body">
                    <a-icon type="message" /> {{ $t("appStore.reply") }}
                </a-button>
                <a-dropdown :trigger="['click']">
                    <a-button size="small">{{ $t("appStore.status") }} <a-icon type="down" /></a-button>
                    <template #overlay>
                        <a-menu @click="(e) => updateStatus(selectedReview, e.key)">
                            <a-menu-item key="new">{{ $t("appStore.new") }}</a-menu-item>
                            <a-menu-item key="replied">{{ $t("appStore.replied") }}</a-menu-item>
                            <a-menu-item key="resolved">{{ $t("appStore.resolved") }}</a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>

        <!-- Empty Detail Panel -->
        <div class="detail-panel empty" v-else>
            <span class="material-icons">rate_review</span>
            <p>{{ $t("appStore.selectReviewToPreview") }}</p>
        </div>

        <!-- Reply Modal -->
        <a-modal
            v-model="replyModal.visible"
            :title="replyModal.isEdit ? $t('appStore.editReply') : $t('appStore.replyToReview')"
            @ok="submitReply"
            :confirm-loading="replyModal.loading"
            width="500px"
        >
            <div class="reply-preview" v-if="replyModal.review">
                <span class="material-icons">account_circle</span>
                <div>
                    <strong>{{ replyModal.review.reviewerNickname || $t("appStore.anonymous") }}</strong>
                    <div class="star-rating-small">
                        <span class="material-icons" v-for="n in replyModal.review.rating" :key="'filled-' + n">star</span>
                    </div>
                    <p v-if="replyModal.review.title">{{ replyModal.review.title }}</p>
                    <p v-if="replyModal.review.body">{{ truncateText(replyModal.review.body, 200) }}</p>
                </div>
            </div>
            <a-textarea
                v-model="replyModal.text"
                :placeholder="$t('appStore.writeYourReply')"
                :rows="4"
            />
        </a-modal>

        <!-- Delete Response Confirmation Modal -->
        <a-modal
            v-model="deleteModal.visible"
            :title="$t('appStore.deleteResponse')"
            @ok="deleteResponse"
            :ok-type="'danger'"
            :confirm-loading="deleteModal.loading"
        >
            <p>{{ $t("appStore.deleteResponseConfirm") }}</p>
        </a-modal>
    </div>
</template>

<script>
import { APP_STORE_API } from "../../../../../api/appstore";
import moment from "moment";

export default {
    name: "ReviewList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    filters: {
        truncate(value, length) {
            if (!value) return "";
            if (value.length <= length) return value;
            return value.substring(0, length) + "...";
        },
    },
    data() {
        return {
            reviews: [],
            apps: [],
            loading: false,
            total: 0,
            selectedReview: null,
            filters: {
                status: "",
                appId: "",
            },
            pagination: {
                page: 1,
                limit: 20,
            },
            replyModal: {
                visible: false,
                review: null,
                text: "",
                loading: false,
                isEdit: false,
            },
            deleteModal: {
                visible: false,
                review: null,
                loading: false,
            },
        };
    },
    methods: {
        getStatusLabel(status) {
            var labels = {
                new: this.$t("appStore.new"),
                replied: this.$t("appStore.replied"),
                resolved: this.$t("appStore.resolved"),
            };
            return labels[status] || status;
        },
        async fetchApps() {
            try {
                const response = await APP_STORE_API.listApps(this.channel._id);
                this.apps = response.data || [];
            } catch (err) {
                console.error("Error fetching apps", err);
            }
        },
        async fetchReviews() {
            this.loading = true;
            try {
                const response = await APP_STORE_API.listReviews({
                    channelId: this.channel._id,
                    appId: this.filters.appId || undefined,
                    status: this.filters.status || undefined,
                    page: this.pagination.page,
                    limit: this.pagination.limit,
                });
                this.reviews = response.data.items;
                this.total = response.data.total;
            } catch (err) {
                this.$message.error(this.$t("appStore.errorFetchingReviews"));
            } finally {
                this.loading = false;
            }
        },
        selectReview(review) {
            this.selectedReview = review;
        },
        async updateStatus(review, status) {
            try {
                await APP_STORE_API.updateStatus(review._id, status);
                review.status = status;
                if (this.selectedReview && this.selectedReview._id === review._id) {
                    this.selectedReview.status = status;
                }
                this.$message.success(this.$t("appStore.statusUpdated"));
            } catch (err) {
                this.$message.error(this.$t("appStore.errorUpdatingStatus"));
            }
        },
        openReplyModal(review, isEdit) {
            this.replyModal.review = review;
            this.replyModal.text = review.response && review.response.body ? review.response.body : "";
            this.replyModal.isEdit = isEdit || false;
            this.replyModal.visible = true;
        },
        async submitReply() {
            if (!this.replyModal.text.trim()) {
                this.$message.warning(this.$t("appStore.pleaseEnterReply"));
                return;
            }
            this.replyModal.loading = true;
            try {
                if (this.replyModal.isEdit) {
                    await APP_STORE_API.updateReviewResponse(this.replyModal.review.reviewId, {
                        body: this.replyModal.text,
                        channelId: this.channel._id,
                    });
                } else {
                    await APP_STORE_API.replyReview(this.replyModal.review.reviewId, {
                        body: this.replyModal.text,
                        channelId: this.channel._id,
                    });
                }
                this.replyModal.review.status = "replied";
                this.replyModal.review.response = {
                    body: this.replyModal.text,
                    lastModifiedDate: new Date(),
                };
                if (this.selectedReview && this.selectedReview._id === this.replyModal.review._id) {
                    this.selectedReview.status = "replied";
                    this.selectedReview.response = this.replyModal.review.response;
                }
                this.$message.success(this.$t("appStore.replySuccess"));
                this.replyModal.visible = false;
            } catch (err) {
                this.$message.error(this.$t("appStore.errorSendingReply"));
            } finally {
                this.replyModal.loading = false;
            }
        },
        confirmDeleteResponse(review) {
            this.deleteModal.review = review;
            this.deleteModal.visible = true;
        },
        async deleteResponse() {
            this.deleteModal.loading = true;
            try {
                await APP_STORE_API.deleteReviewResponse(this.deleteModal.review.reviewId, this.channel._id);
                this.deleteModal.review.response = null;
                this.deleteModal.review.status = "new";
                if (this.selectedReview && this.selectedReview._id === this.deleteModal.review._id) {
                    this.selectedReview.response = null;
                    this.selectedReview.status = "new";
                }
                this.$message.success(this.$t("appStore.responseDeleted"));
                this.deleteModal.visible = false;
            } catch (err) {
                this.$message.error(this.$t("appStore.errorDeletingResponse"));
            } finally {
                this.deleteModal.loading = false;
            }
        },
        onPageChange(page) {
            this.pagination.page = page;
            this.fetchReviews();
        },
        onPageSizeChange(current, size) {
            this.pagination.limit = size;
            this.pagination.page = 1;
            this.fetchReviews();
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        truncateText(text, length) {
            if (!text) return "";
            if (text.length <= length) return text;
            return text.substring(0, length) + "...";
        },
    },
    watch: {
        "filters.status"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
        "filters.appId"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
    },
    mounted() {
        this.fetchApps();
        this.fetchReviews();
    },
};
</script>

<style lang="scss" scoped>
.appstore-reviews-page {
    display: flex;
    gap: 20px;
    height: calc(100vh - 200px);
    padding: 16px;

    .reviews-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;

        .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            border-bottom: 1px solid var(--color-border-default);
            flex-wrap: wrap;
            gap: 12px;

            .panel-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                font-weight: 600;
                margin: 0;

                .apple-icon {
                    color: #007aff;
                    font-size: 20px;
                }

                .count-badge {
                    background: var(--color-bg-subtle);
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
            }

            .header-actions {
                display: flex;
                gap: 8px;
                align-items: center;
            }
        }

        .reviews-list {
            flex: 1;
            overflow-y: auto;
            padding: 8px;

            .review-item {
                display: flex;
                gap: 12px;
                padding: 12px;
                border-radius: 8px;
                cursor: pointer;
                transition: background 0.15s;
                border: 1px solid transparent;

                &:hover {
                    background: var(--color-bg-subtle);
                }

                &.selected {
                    background: var(--color-bg-subtle);
                    border-color: var(--color-border-strong);
                }

                .avatar-placeholder {
                    font-size: 36px;
                    color: var(--color-text-default);
                    flex-shrink: 0;
                }

                .review-main {
                    flex: 1;
                    min-width: 0;

                    .review-top {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 4px;

                        .author-name {
                            font-weight: 600;
                            font-size: 13px;
                        }

                        .review-date {
                            font-size: 11px;
                            color: var(--color-text-default);
                        }

                        .status-dot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            margin-left: auto;

                            &.new { background: #007aff; }
                            &.replied { background: #34c759; }
                            &.resolved { background: #8c8c8c; }
                        }
                    }

                    .star-rating {
                        margin-bottom: 6px;

                        .material-icons {
                            font-size: 14px;
                            color: #ff9500;

                            &.empty {
                                color: #e0e0e0;
                            }
                        }
                    }

                    .review-title {
                        font-weight: 600;
                        font-size: 13px;
                        margin: 0 0 4px;
                    }

                    .review-text {
                        font-size: 13px;
                        line-height: 1.5;
                        margin: 0 0 6px;
                        color: var(--color-text-strong-default);

                        &.no-comment {
                            font-style: italic;
                            color: var(--color-text-default);
                        }
                    }

                    .review-bottom {
                        display: flex;
                        gap: 12px;
                        font-size: 11px;
                        color: var(--color-text-default);
                        align-items: center;

                        .app-link {
                            display: flex;
                            align-items: center;
                            gap: 4px;

                            .material-icons {
                                font-size: 14px;
                                color: #007aff;
                            }
                        }

                        .territory-badge {
                            background: var(--color-bg-subtle);
                            padding: 2px 6px;
                            border-radius: 4px;
                            font-family: monospace;
                        }
                    }
                }

                .review-actions-mini {
                    opacity: 0;
                    transition: opacity 0.15s;
                }

                &:hover .review-actions-mini {
                    opacity: 1;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 60px 20px;
                color: var(--color-text-default);

                .material-icons {
                    font-size: 48px;
                    margin-bottom: 12px;
                    opacity: 0.5;
                }
            }
        }

        .loading-state {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
        }

        .pagination-wrapper {
            padding: 12px 16px;
            border-top: 1px solid var(--color-border-default);
            display: flex;
            justify-content: center;
        }
    }

    .detail-panel {
        width: 380px;
        flex-shrink: 0;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 16px;

        &.empty {
            justify-content: center;
            align-items: center;
            color: var(--color-text-default);

            .material-icons {
                font-size: 48px;
                margin-bottom: 12px;
                opacity: 0.4;
            }

            p {
                font-size: 14px;
                margin: 0;
            }
        }

        .detail-header {
            display: flex;
            gap: 10px;
            margin-bottom: 12px;

            .material-icons {
                font-size: 40px;
                color: var(--color-text-default);
            }

            .author-details {
                flex: 1;

                .name {
                    display: block;
                    font-weight: 600;
                    font-size: 14px;
                }

                .date {
                    font-size: 12px;
                    color: var(--color-text-default);
                }
            }

            .badges {
                .status-badge {
                    padding: 2px 8px;
                    border-radius: 10px;
                    font-size: 10px;

                    &.new { background: #e5f1ff; color: #007aff; }
                    &.replied { background: #e8f9ed; color: #34c759; }
                    &.resolved { background: #f5f5f5; color: #8c8c8c; }
                }
            }
        }

        .star-rating-large {
            margin-bottom: 12px;

            .material-icons {
                font-size: 24px;
                color: #ff9500;

                &.empty {
                    color: #e0e0e0;
                }
            }
        }

        .detail-title {
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 8px;
        }

        .detail-body {
            font-size: 14px;
            line-height: 1.6;
            margin-bottom: 16px;
            padding: 12px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            &.no-comment {
                font-style: italic;
                color: var(--color-text-default);
            }
        }

        .territory-info {
            margin-bottom: 16px;
            padding: 12px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            .section-title {
                font-weight: 600;
                font-size: 12px;
                color: var(--color-text-default);
                margin-bottom: 8px;
            }

            .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 8px;

                .info-item {
                    font-size: 12px;

                    .label {
                        color: var(--color-text-default);
                    }

                    .value {
                        font-weight: 500;
                    }
                }
            }
        }

        .reply-section {
            margin-bottom: 16px;
            padding: 12px;
            background: #e8f9ed;
            border-radius: 8px;
            border-left: 3px solid #34c759;

            .reply-header {
                display: flex;
                align-items: center;
                gap: 4px;
                font-weight: 600;
                font-size: 12px;
                color: #34c759;
                margin-bottom: 8px;

                .material-icons {
                    font-size: 16px;
                }

                .reply-state {
                    margin-left: auto;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 10px;
                    background: rgba(0,0,0,0.1);

                    &.PUBLISHED { background: #34c759; color: #fff; }
                    &.PENDING_PUBLISH { background: #ff9500; color: #fff; }
                }
            }

            .reply-content {
                font-size: 13px;
                line-height: 1.5;
            }

            .reply-date {
                font-size: 11px;
                color: var(--color-text-default);
                margin-top: 8px;
            }

            .reply-actions {
                display: flex;
                gap: 8px;
                margin-top: 10px;
            }
        }

        .detail-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-top: auto;
        }
    }

    .reply-preview {
        display: flex;
        gap: 12px;
        padding: 12px;
        background: var(--color-bg-subtle);
        border-radius: 8px;
        margin-bottom: 16px;

        .material-icons {
            font-size: 32px;
            color: var(--color-text-default);
        }

        .star-rating-small {
            .material-icons {
                font-size: 12px;
                color: #ff9500;
            }
        }

        p {
            margin: 4px 0 0;
            font-size: 13px;
            color: var(--color-text-default);
        }
    }

    .status-indicator {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;

        &.new { background: #007aff; }
        &.replied { background: #34c759; }
        &.resolved { background: #8c8c8c; }
    }
}
</style>
